import { createContext, useContext, useEffect, useState } from "react";
import { usePixiBillingClient } from "../../billing/usePixiBillingClient";
import { Plan } from "src/billinglatest/types";
import { formatted } from "src/billinglatest/util/currency";
import { BillingCycle, CurrencyCode, PlanId } from "./constants";

/**
 * Onboarding plan catalog (v4 = Core/Pro). Prices come from the billing API
 * (`searchPlans`) — never hardcoded. `searchPlans` builds its client from the
 * session token, so it works in the onboarding tree without BillingContext;
 * it returns `undefined` when there is no auth token, which we treat as an error.
 */
export interface OnboardingPlans {
  plans: Plan[];
  loading: boolean;
  error: boolean;
  /**
   * Find the plan for a tier + billing cycle + currency, or undefined. Resolves
   * by the canonical plan id (`{group}-{cycle}-{currency}`, e.g.
   * `core-year-gbp`) rather than by matching the group/term/currency fields —
   * the v4 catalog can return more than one entry matching those fields (a
   * legacy £0 variant alongside the current plan), and a field match would pick
   * the wrong one (GRWBKK-1669).
   */
  getPlan: (
    group: PlanId,
    cycle: BillingCycle,
    currency: CurrencyCode,
  ) => Plan | undefined;
}

const matchesOnboarding = (plan: Plan): boolean =>
  (plan.group === "core" || plan.group === "pro") &&
  plan.termNumber === 1 &&
  (plan.term === "month" || plan.term === "year");

export const useOnboardingPlans = (): OnboardingPlans => {
  const billing = usePixiBillingClient();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Fetch every currency once on mount, then filter client-side, so changing
  // currency or billing cycle never triggers another request.
  useEffect(() => {
    // Waits for the billing version; loading stays true meanwhile.
    if (!billing) {
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(false);
      try {
        const result = await billing.listPlans();
        if (cancelled) return;
        if (!result) {
          // No billing client / no auth token.
          setError(true);
          setPlans([]);
          return;
        }
        setPlans(result.filter(matchesOnboarding));
      } catch {
        if (!cancelled) {
          setError(true);
          setPlans([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [billing]);

  const getPlan = (
    group: PlanId,
    cycle: BillingCycle,
    currency: CurrencyCode,
  ): Plan | undefined => {
    const planId = `${group}-${cycle}-${currency.toLowerCase()}`;
    return plans.find((plan) => plan.id === planId);
  };

  return { plans, loading, error, getPlan };
};

/**
 * Per-license price actually billed for a selection, in the plan's own period
 * (cents). The Yearly + free-device deal is billed at the *monthly* rate × 12:
 * the free Pixi replaces the annual discount, so the plan reverts to its
 * undiscounted monthly rate rather than the (lower) yearly-per-month rate.
 * Every other selection — Monthly, or Yearly + discount — bills at the plan's
 * own catalogue price. Prices always come from the billing catalogue
 * (`searchPlans`), never hardcoded (GRWBKK-1695).
 *
 * NB this intentionally supersedes two lines of the ticket's written AC — "the
 * free Pixi offer should not change the displayed plan price" and "checkout
 * uses the selected yearly plan total" — per the assignee's direction: the
 * free-device plan price is the monthly rate, and its checkout total is that
 * monthly rate × 12 (higher than the discounted yearly total). Monthly and
 * discount pricing are unchanged.
 */
export const billingPeriodPriceCents = (
  plan: Plan,
  monthlyPlan: Plan | undefined,
  isFreeDevice: boolean,
): number =>
  isFreeDevice && plan.term === "year" && monthlyPlan
    ? monthlyPlan.price * 12
    : plan.price;

/**
 * Per-screen monthly price for display (the design shows a monthly figure even
 * when billed yearly). Yearly plans store the annual per-license price, so we
 * divide by 12; monthly plans are already per-month. Deal-aware via
 * `billingPeriodPriceCents` so Yearly + free-device shows the monthly rate.
 * Prices are in cents.
 */
export const perScreenMonthlyPrice = (
  plan: Plan,
  monthlyPlan?: Plan,
  isFreeDevice = false,
): string => {
  const periodCents = billingPeriodPriceCents(plan, monthlyPlan, isFreeDevice);
  const cents = plan.term === "year" ? periodCents / 12 : periodCents;
  return formatted(plan.currency.toUpperCase(), cents);
};

/**
 * Footer/checkout total = per-license price × license count, in the plan's
 * period. Deal-aware via `billingPeriodPriceCents` so the display, footer and
 * checkout all route the free-device rule through one predicate.
 */
export const planTotal = (
  plan: Plan,
  licenses: number,
  monthlyPlan?: Plan,
  isFreeDevice = false,
): string =>
  formatted(
    plan.currency.toUpperCase(),
    billingPeriodPriceCents(plan, monthlyPlan, isFreeDevice) * licenses,
  );

/** Raw savings (in cents) for the free-device deal — one device per license. */
export const deviceDealSavingsCents = (
  perDeviceCents: number,
  licenses: number,
): number => perDeviceCents * licenses;

/** Context value shared between the Plan & Billing step and the footer total. */
export interface PlanBillingContextValue extends OnboardingPlans {
  /** Pro-trialist users see only Pro (Monthly/Annual) with no cycle toggle. */
  isProTrialist: boolean;
  /**
   * Studio org country (`currentOrg.preferences.settings.country_code`), used to
   * default the free-device "Ship to" country (GRWBKK-1730). Undefined when the
   * org has no country set — the flow then keeps the historical US default.
   */
  orgCountryCode?: string;
}

export const OnboardingPlansContext =
  createContext<PlanBillingContextValue | null>(null);

export const useOnboardingPlansContext = (): PlanBillingContextValue => {
  const ctx = useContext(OnboardingPlansContext);
  if (!ctx) {
    throw new Error(
      "useOnboardingPlansContext must be used within OnboardingPlansContext.Provider",
    );
  }
  return ctx;
};
